/* eslint-disable max-lines */
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, withLatestFrom } from 'rxjs';
import { UserConfigService } from 'src/app/services/utility/user-config.service';
import { eventsMonitorSource, GRID_BASE_SETTINGS_KEY } from '../events-monitor.constants';
import { map, exhaustMap, catchError, switchMap, take, tap, filter, mergeMap } from 'rxjs/operators';
import { EventsMonitorActions } from './events-monitor.actions';
import { EventsMonitorService } from '../services/events-monitor.service';
import {
  selectChartInteractionState,
  selectFiltersLoaded,
  selectPayload,
  selectIsTrendsAllActiveSelected,
  selectDateRange,
  selectActiveTab
} from './events-monitor.reducer';
import { DevicesPayload } from 'src/app/pages/equipment/devices/components/devices.models';
import { TableData } from 'src/app/shared/types/api-response.types';
import { Events, EventsMonitorTabValue, UserConfigEventMonitorFilters } from '../events-monitor.types';
import { ExportDataService } from 'src/app/services/file/export-data.service';
import { EventsMonitorModelService } from '../services/events-monitor.model.service';
import { EventsMonitorApiService } from '../services/events-monitor.api.service';
import { MapReportGroupActions } from '@portal/shared/components/map-report-group/store/map-report-group.actions';
import { selectSelectedArea } from '@portal/shared/components/map-report-group/store/map-report-group.reducer';
import { EventsMonitorFiltersSourceService } from '../services/events-monitor-filters-source.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { createDefaultDateRange, createDateRangeFilter } from '../events-monitor.utils';

export const getFiltersState$ = createEffect(
  (
    actions$ = inject(Actions),
    eventsMonitorService = inject(EventsMonitorService),
    userConfigService = inject(UserConfigService)
  ) => {
    return actions$.pipe(
      ofType(EventsMonitorActions.getPersistedFiltersState),
      exhaustMap(() =>
        userConfigService.getGridSettings<UserConfigEventMonitorFilters>(GRID_BASE_SETTINGS_KEY).pipe(
          map(s => {
            const filtersState = eventsMonitorService.extractPersistedFilters(s?.filters ?? null);
            const restoredTake = s?.pageSize ?? null;
            return EventsMonitorActions.getFiltersAndInitFiltersState({
              filtersState,
              ...(restoredTake != null && { restoredTake }),
              columnsConfig: s
                ? { columnWidth: s.columnWidth, columnOrder: s.columnOrder, columnVisibility: s.columnVisibility }
                : null
            });
          }),
          catchError(() =>
            of(EventsMonitorActions.getFiltersAndInitFiltersState({ filtersState: null, columnsConfig: null }))
          )
        )
      )
    );
  },
  { functional: true }
);

export const getFiltersAndInitFiltersState$ = createEffect(
  (
    actions$ = inject(Actions),
    eventsMonitorFiltersSourceService = inject(EventsMonitorFiltersSourceService),
    timeZoneService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(EventsMonitorActions.getFiltersAndInitFiltersState),
      exhaustMap(({ filtersState, restoredTake, columnsConfig }) =>
        eventsMonitorFiltersSourceService.getAll().pipe(
          switchMap(filters =>
            of(
              EventsMonitorActions.getFiltersAndInitFiltersStateSuccess({
                ...filters,
                filtersState,
                ...(restoredTake !== undefined && { restoredTake }),
                columnsConfig,
                defaultDateRangeFilterUtc: createDateRangeFilter(
                  timeZoneService.getDatepickerRange(createDefaultDateRange())
                )
              })
            )
          ),
          catchError(() =>
            of(
              EventsMonitorActions.getFiltersAndInitFiltersStateSuccess({
                filters: [],
                eventsRecord: {},
                filtersState: null,
                columnsConfig: null,
                defaultDateRangeFilterUtc: createDateRangeFilter(
                  timeZoneService.getDatepickerRange(createDefaultDateRange())
                )
              })
            )
          )
        )
      )
    ),
  { functional: true }
);

export const refresh$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(EventsMonitorActions.refresh),
      switchMap(() => of(EventsMonitorActions.getPersistedFiltersState()))
    ),
  { functional: true }
);

export const tableData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(
        EventsMonitorActions.fetchTableData,
        EventsMonitorActions.resetSelectedFromMapButton,
        EventsMonitorActions.updateChartSelection,
        EventsMonitorActions.resetChartSelection,
        EventsMonitorActions.updateEventTrendsType
      ),
      switchMap(() =>
        combineLatest([
          store.select(selectPayload).pipe(take(1)),
          store.select(selectChartInteractionState).pipe(take(1)),
          store.select(selectFiltersLoaded).pipe(take(1)),
          store.select(selectIsTrendsAllActiveSelected).pipe(take(1))
        ]).pipe(
          filter(([, , filtersLoaded]) => filtersLoaded),
          exhaustMap(([payload, chartState, , isTrendsAllActive]) =>
            eventsMonitorService
              .getTableData({
                ...payload,
                filters: eventsMonitorService.getShiftedDatePayload(payload.filters, isTrendsAllActive, chartState)
              })
              .pipe(
                map(data => EventsMonitorActions.fetchTableDataSuccess({ data })),
                catchError(() => of(EventsMonitorActions.fetchTableDataFailure({ error: 'Failed to load table data' })))
              )
          )
        )
      )
    ),
  { functional: true }
);

export const totalWidgetData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(
        EventsMonitorActions.getWidgetData,
        EventsMonitorActions.updateEventTrendsType,
        EventsMonitorActions.resetSelectedFromMapButton
      ),
      withLatestFrom(store.select(selectPayload), store.select(selectIsTrendsAllActiveSelected)),
      exhaustMap(([, { filters, search }, isTrendsAllActive]) =>
        eventsMonitorService
          .getWidgetTotalCount({
            filters: eventsMonitorService.getShiftedDatePayload(filters, isTrendsAllActive),
            search
          })
          .pipe(
            map(total => EventsMonitorActions.fetchTotalEventsWidgetSuccess({ data: total })),
            catchError(() =>
              of(EventsMonitorActions.fetchTotalEventsWidgetFailure({ error: 'Failed to load total events widget' }))
            )
          )
      )
    ),
  { functional: true }
);

export const severityWidgetData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(
        EventsMonitorActions.getWidgetData,
        EventsMonitorActions.updateEventTrendsType,
        EventsMonitorActions.resetSelectedFromMapButton
      ),
      withLatestFrom(store.select(selectPayload), store.select(selectIsTrendsAllActiveSelected)),
      exhaustMap(([, { filters, search }, isTrendsAllActive]) =>
        eventsMonitorService
          .getWidgetSeverity({
            filters: eventsMonitorService.getShiftedDatePayload(filters, isTrendsAllActive),
            search
          })
          .pipe(
            map(severity => EventsMonitorActions.fetchSeverityWidgetSuccess({ data: severity })),
            catchError(() =>
              of(EventsMonitorActions.fetchSeverityWidgetFailure({ error: 'Failed to load severity widget' }))
            )
          )
      )
    ),
  { functional: true }
);

export const eventGroupWidgetData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(
        EventsMonitorActions.getWidgetData,
        EventsMonitorActions.updateEventTrendsType,
        EventsMonitorActions.resetSelectedFromMapButton
      ),
      withLatestFrom(store.select(selectPayload), store.select(selectIsTrendsAllActiveSelected)),
      exhaustMap(([, { filters, search }, isTrendsAllActive]) =>
        eventsMonitorService
          .getWidgetEventGroup({
            filters: eventsMonitorService.getShiftedDatePayload(filters, isTrendsAllActive),
            search
          })
          .pipe(
            map(groups => EventsMonitorActions.fetchEventGroupWidgetSuccess({ data: groups })),
            catchError(() =>
              of(EventsMonitorActions.fetchEventGroupWidgetFailure({ error: 'Failed to load event group widget' }))
            )
          )
      )
    ),
  { functional: true }
);

export const durationGroupWidgetData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(
        EventsMonitorActions.getWidgetData,
        EventsMonitorActions.updateEventTrendsType,
        EventsMonitorActions.resetSelectedFromMapButton
      ),
      withLatestFrom(store.select(selectPayload), store.select(selectIsTrendsAllActiveSelected)),
      exhaustMap(([, { filters, search }, isTrendsAllActive]) =>
        eventsMonitorService
          .getWidgetDurationGroup({
            filters: eventsMonitorService.getShiftedDatePayload(filters, isTrendsAllActive),
            search
          })
          .pipe(
            map(groups => EventsMonitorActions.fetchDurationGroupWidgetSuccess({ data: groups })),
            catchError(() =>
              of(
                EventsMonitorActions.fetchDurationGroupWidgetFailure({
                  error: 'Failed to load duration group widget'
                })
              )
            )
          )
      )
    ),
  { functional: true }
);

export const trendsData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(EventsMonitorActions.fetchTrends, EventsMonitorActions.updateEventTrendsType),
      withLatestFrom(store.select(selectPayload), store.select(selectIsTrendsAllActiveSelected)),
      exhaustMap(([, { filters, search }, isTrendsAllActive]) =>
        eventsMonitorService
          .getTrends({
            filters: eventsMonitorService.getShiftedDatePayload(filters, isTrendsAllActive),
            search,
            interval: 'Day'
          })
          .pipe(
            map(trends => EventsMonitorActions.fetchTrendsSuccess({ data: trends })),
            catchError(() => of(EventsMonitorActions.fetchTrendsFailure({ error: 'Failed to load trends data' })))
          )
      )
    ),
  { functional: true }
);

export const exportTableData$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    eventsMonitorService = inject(EventsMonitorService),
    eventsMonitorModel = inject(EventsMonitorModelService),
    exportDataService = inject(ExportDataService)
  ) =>
    actions$.pipe(
      ofType(EventsMonitorActions.fileExport),
      withLatestFrom(
        store.select(selectPayload),
        store.select(selectChartInteractionState),
        store.select(selectIsTrendsAllActiveSelected)
      ),
      switchMap(([action, payload, chartState, isTrendsAllActiveSelected]) =>
        eventsMonitorService
          .getTableData({
            ...payload,
            filters: eventsMonitorService.getShiftedDatePayload(payload.filters, isTrendsAllActiveSelected, chartState)
          })
          .pipe(
            take(1),
            map(res => ({
              dataSource: {
                getAll: (tableState: DevicesPayload): Observable<TableData<Events>> =>
                  eventsMonitorService.getTableData(tableState).pipe(take(1)),
                total: res.total
              },
              exportCsv: eventsMonitorModel.exportCsv
            })),
            tap(listingPageModel => {
              const { visibility, order } = action;
              exportDataService.exportTableToCsv(
                {
                  ...payload,
                  filters: eventsMonitorService.getShiftedDatePayload(
                    payload.filters,
                    isTrendsAllActiveSelected,
                    chartState
                  )
                },
                listingPageModel,
                visibility,
                order
              );
            }),
            map(() => EventsMonitorActions.fileExportSuccess()),
            catchError(err => {
              console.error('Error fetching data for export:', err);
              return of(EventsMonitorActions.fileExportFailure({ error: err?.message ?? 'Export failed' }));
            })
          )
      )
    ),
  { functional: true }
);

export const devicesSelected$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorApiService = inject(EventsMonitorApiService)) =>
    actions$.pipe(
      ofType(MapReportGroupActions.fetchDevicesForSelectedArea),
      filter(({ source }) => source === eventsMonitorSource),
      withLatestFrom(store.select(selectSelectedArea)),
      exhaustMap(([{ filters, search }, selectedArea]) =>
        eventsMonitorApiService
          .getMapAreaDevicesFromEvents({
            coordinates: selectedArea.map(item => ({
              longitude: item[0],
              latitude: item[1]
            })),
            filters: [filters],
            search
          })
          .pipe(
            map(devicesResponse =>
              MapReportGroupActions.fetchDevicesForSelectedAreaSuccess({ deviceIds: devicesResponse.devices })
            ),
            catchError(() => of(MapReportGroupActions.fetchDevicesForSelectedAreaSuccess({ deviceIds: [] })))
          )
      )
    ),
  { functional: true }
);

export const reportGroupFiltersRefetched$ = createEffect(
  (actions$ = inject(Actions), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(MapReportGroupActions.createReportGroupSuccess),
      filter(({ source }) => source === eventsMonitorSource),
      exhaustMap(() =>
        eventsMonitorService.getReportGroup('Network Monitoring').pipe(
          map(reportGroups => EventsMonitorActions.fetchReportGroupSuccess({ data: reportGroups })),
          catchError(() => of(EventsMonitorActions.fetchReportGroupFailure({ error: 'Failed to load report groups' })))
        )
      )
    ),
  { functional: true }
);

export const saveFiltersState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), eventsMonitorService = inject(EventsMonitorService)) =>
    actions$.pipe(
      ofType(EventsMonitorActions.savePersistedFiltersState),
      withLatestFrom(store.select(selectPayload), store.select(selectDateRange)),
      tap(([, payload, dateRange]) => {
        eventsMonitorService.persistFilters({
          filters: payload.filters,
          dateRange: dateRange!
        });
      })
    ),
  { functional: true, dispatch: false }
);

export const fetchAllPageData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(EventsMonitorActions.getFiltersAndInitFiltersStateSuccess, EventsMonitorActions.updateActiveTab),
      withLatestFrom(store.select(selectActiveTab)),
      mergeMap(([action, activeTab]) => {
        const shouldFetchTableData =
          activeTab === EventsMonitorTabValue.Table || activeTab === EventsMonitorTabValue.Trends;
        const shouldFetchTrendsData = activeTab === EventsMonitorTabValue.Trends;
        const shouldFetchWidgetData = action.type === EventsMonitorActions.getFiltersAndInitFiltersStateSuccess.type;

        return [
          EventsMonitorActions.resetChartSelection(),
          ...(shouldFetchWidgetData ? [EventsMonitorActions.getWidgetData()] : []),
          ...(shouldFetchTableData ? [EventsMonitorActions.fetchTableData()] : []),
          ...(shouldFetchTrendsData ? [EventsMonitorActions.fetchTrends()] : [])
        ];
      })
    ),
  { functional: true }
);
